import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Skeleton } from "../../components/Skeleton/Skeleton";

import { ApiError, describeError } from "../../lib/errors";
import { getProject } from "../projects/api";
import { getGuild } from "./api";
import { GuildChatFeed } from "./GuildChatFeed";
import { GuildMembersTable } from "./GuildMembersTable";
import { GuildSettingsForm } from "./GuildSettingsForm";
import type { GuildDetail } from "./types";
import styles from "./GuildDetailPage.module.css";

// === Formatting helpers ============================================== //
// Copied from PlayerDetailPage — consistent with the players feature, which
// keeps a local copy rather than a shared module.

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Feb 28, 2026" — used for the Created field of the identity card. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return DATE_FMT.format(d);
}

/**
 * Compact relative time — "just now", "2 hours ago", "1 day ago", or an
 * absolute date past a week. Used for the "Last updated" identity field.
 */
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  return DATE_FMT.format(d);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

// === Component ====================================================== //

/**
 * The guild detail page — ports docs/mockups/guild-detail.html screen 1. It
 * orchestrates a read-only identity header plus the three section components:
 * GuildSettingsForm, GuildMembersTable and GuildChatFeed. The guild query is
 * the shared baseline: the Settings form re-syncs it via `onSaved`, the Members
 * table invalidates it via `onChanged`.
 */
export function GuildDetailPage() {
  const { id = "", guildId = "" } = useParams<{
    id: string;
    guildId: string;
  }>();
  const qc = useQueryClient();

  // The project — shared cache key with GuildsListPage / other project pages.
  // Used only for the breadcrumb name.
  const projectQuery = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id).then((r) => r.data),
    enabled: !!id,
  });

  // The guild record + its members.
  const guildQuery = useQuery({
    queryKey: ["guild", id, guildId],
    queryFn: () => getGuild(id, guildId),
    enabled: !!id && !!guildId,
  });

  const project = projectQuery.data;
  const guild = guildQuery.data;

  const header = (
    <PageHeader
      breadcrumb={[
        { label: project?.name ?? "Project" },
        { label: "Guilds", to: `/projects/${id}/guilds` },
        { label: guild?.name ?? "Guild" },
      ]}
      title={guild?.name ?? "Guild"}
      actions={
        <Link to={`/projects/${id}/guilds`} className={styles.backBtn}>
          <ChevronLeft size={13} aria-hidden />
          Back to guilds
        </Link>
      }
    />
  );

  // === Not-found state =============================================== //
  if (
    guildQuery.isError &&
    guildQuery.error instanceof ApiError &&
    guildQuery.error.status === 404
  ) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Guild not found">
                This guild doesn&apos;t exist, or it belongs to another
                project.
              </Alert>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Generic load error =========================================== //
  if (projectQuery.isError || guildQuery.isError) {
    const err =
      guildQuery.error instanceof ApiError
        ? guildQuery.error
        : projectQuery.error instanceof ApiError
          ? projectQuery.error
          : null;
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Couldn't load guild">
                {err ? describeError(err.code) : describeError("internal")}
              </Alert>
              <div className={styles.errorActions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void projectQuery.refetch();
                    void guildQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Loading skeleton ============================================= //
  if (projectQuery.isLoading || guildQuery.isLoading || !project || !guild) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <LoadingSkeleton />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <div className={styles.page}>
        <div className={styles.content}>
          {/* ───────── Identity (read-only) ───────── */}
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <div className={styles.identity}>
                <span className={styles.emLg}>
                  <Avatar name={guild.name} size={52} />
                </span>
                <div>
                  <div className={styles.identityName}>{guild.name}</div>
                  <div className={styles.identitySub}>
                    <VisibilityBadge visibility={guild.visibility} />
                    <span>
                      · {guild.member_count}{" "}
                      {guild.member_count === 1 ? "member" : "members"}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.kvGrid}>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Guild UUID</div>
                  <div className={`${styles.kvVal} ${styles.mono}`}>
                    {guild.id}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Created</div>
                  <div className={styles.kvVal}>
                    {formatDate(guild.created_at)}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Last updated</div>
                  <div className={styles.kvVal}>
                    {formatRelative(guild.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Settings ───────── */}
          {/* Keyed on `updated_at` so the form remounts (re-seeding its field
              state) once the invalidated query refetches after a save. */}
          <GuildSettingsForm
            key={guild.updated_at}
            projectId={id}
            guildId={guildId}
            guild={guild}
            onSaved={(updated) => {
              qc.setQueryData<GuildDetail>(["guild", id, guildId], updated);
              void qc.invalidateQueries({
                queryKey: ["guild", id, guildId],
              });
              void qc.invalidateQueries({
                queryKey: ["project", id, "activity"],
              });
            }}
          />

          {/* ───────── Members ───────── */}
          <GuildMembersTable
            projectId={id}
            guildId={guildId}
            guild={guild}
            onChanged={() => {
              void qc.invalidateQueries({ queryKey: ["guild", id, guildId] });
              void qc.invalidateQueries({
                queryKey: ["project", id, "activity"],
              });
            }}
          />

          {/* ───────── Chat ───────── */}
          <GuildChatFeed projectId={id} guildId={guildId} />
        </div>
      </div>
    </>
  );
}

// === Visibility badge ================================================ //
// Mirrors GuildsListPage's VisibilityBadge — the mockup `.vis-badge`.

function VisibilityBadge({ visibility }: { visibility: string }) {
  const isOpen = visibility === "open";
  return (
    <span
      className={`${styles.visBadge} ${isOpen ? styles.open : styles.invite}`}
    >
      <span className={styles.d} />
      {isOpen ? "Open" : "Invite only"}
    </span>
  );
}

// === Loading skeleton =============================================== //
// Identity card + 3 section placeholders (Settings / Members / Chat).

function LoadingSkeleton() {
  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.identity}>
            <Skeleton width={52} height={52} radius={13} />
            <div className={styles.skelLines}>
              <Skeleton width={160} height={18} />
              <Skeleton width={120} height={12} />
            </div>
          </div>
          <div className={styles.kvGrid}>
            {[0, 1, 2].map((i) => (
              <div className={styles.kv} key={i}>
                <Skeleton width={70} height={9} />
                <Skeleton width="80%" height={13} style={{ marginTop: 4 }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {[0, 1, 2].map((i) => (
        <div className={styles.card} key={i}>
          <div className={styles.skelHeader}>
            <Skeleton width={90} height={14} />
          </div>
          <div className={styles.cardBody}>
            <Skeleton width={120} height={12} />
            <Skeleton
              width="100%"
              height={36}
              radius={6}
              style={{ marginTop: 6 }}
            />
          </div>
        </div>
      ))}
    </>
  );
}
